/**
 * ProductDetail tab-content panels — Features / Specifications / Learn.
 *
 * Extracted from ProductDetailPage.jsx in the Feb 2026 P2 refactor. Each
 * component is a pure function of ``product`` so the parent doesn't need
 * to pass a dozen props for the tab bodies.
 */
import { Link } from 'react-router-dom';
import { FileText, Info } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';

export function ProductFeaturesTab({ product }) {
  const features = product.keyFeatures || product.features || [];
  return (
    <TabsContent value="features" className="pt-4">
      <ul className="space-y-3">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Product Sheet — opens PDF inline in a new browser tab */}
      {product.productSheetPdf && product.categoryId !== 'accessories' && (
        <Button
          asChild
          variant="default"
          className="w-full mt-6"
          data-testid="product-sheet-btn"
        >
          <a href={product.productSheetPdf} target="_blank" rel="noopener noreferrer">
            <FileText className="w-4 h-4 mr-2" />
            Product Sheet
          </a>
        </Button>
      )}
    </TabsContent>
  );
}

function TechSpecsButton({ href, className = 'w-full mt-4' }) {
  return (
    <Button asChild variant="default" className={className} data-testid="technical-specs-btn">
      <a href={href} target="_blank" rel="noopener noreferrer">
        <FileText className="w-4 h-4 mr-2" />
        Technical Specs
      </a>
    </Button>
  );
}

export function ProductSpecificationsTab({ product }) {
  const specs = product.specifications && Object.keys(product.specifications).length > 0
    ? product.specifications
    : null;

  return (
    <TabsContent value="specifications" className="pt-4">
      {specs ? (
        <div className="space-y-3">
          {Object.entries(specs).map(([key, value]) => {
            // Support both plain string values and `{ label, value }` objects
            // so custom labels (slashes, punctuation, etc.) aren't constrained
            // by the auto camelCase → Title conversion.
            const isObj = value !== null && typeof value === 'object' && 'value' in value;
            const label = isObj && value.label
              ? value.label
              : key.replace(/([A-Z])/g, ' $1').trim();
            const displayValue = isObj ? value.value : value;
            return (
              <div key={key} className="flex justify-between py-2 border-b border-border">
                <span className={`text-sm font-medium ${isObj && value.label ? '' : 'capitalize'}`}>
                  {label}
                </span>
                <span className="text-sm text-muted-foreground text-right">{displayValue}</span>
              </div>
            );
          })}
          {product.techSpecsPdf && <TechSpecsButton href={product.techSpecsPdf} />}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-muted-foreground">Detailed specifications coming soon.</p>
          {product.techSpecsPdf && (
            <TechSpecsButton href={product.techSpecsPdf} className="w-full" />
          )}
        </div>
      )}
    </TabsContent>
  );
}

export function ProductLearnTab({ product }) {
  return (
    <TabsContent value="learn" className="pt-4">
      <div className="space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            Expert Insight
          </h4>
          <p className="text-sm text-muted-foreground">
            Our flooring specialists recommend {product.name} for commercial spaces
            requiring both durability and aesthetic appeal. Contact us for
            personalized advice.
          </p>
        </div>
        <Link to="/contact">
          <Button className="w-full" data-testid="speak-to-expert-btn">
            Speak to an Expert
          </Button>
        </Link>
      </div>
    </TabsContent>
  );
}
